import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './CanvasNameModal.css';

interface CanvasNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (canvasName: string) => Promise<void>;
  title?: string;
  placeholder?: string;
}

const CanvasNameModal: React.FC<CanvasNameModalProps> = ({
  isOpen,
  onClose,
  onSave,
  title = "Name Your Canvas",
  placeholder = "Enter canvas name..."
}) => {
  const [canvasName, setCanvasName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setCanvasName('');
      // Focus the input when modal opens
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (canvasName.trim() && !isLoading) {
      setIsLoading(true);
      try {
        await onSave(canvasName.trim());
        // Only close modal after saving is completely done
        onClose();
      } catch (error) {
        console.error('Error saving canvas:', error);
        // Don't close modal on error so user can retry
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleInputPointerDown = (e: React.PointerEvent) => {
    // Prevent backdrop click when interacting with input
    e.stopPropagation();
  };

  const handleModalPointerDown = (e: React.PointerEvent) => {
    // Prevent any dragging behavior when modal is open
    e.stopPropagation();
  };

  if (!isOpen) return null;

  const modal = (
    <div className="canvas-name-modal-backdrop" onClick={handleBackdropClick}>
      <div className="canvas-name-modal" onPointerDown={handleModalPointerDown}>
        <div className="canvas-name-modal-content">
          <h3>{title}</h3>
          <p className="canvas-name-description">
            Give your canvas a name to save your work and continue creating.
          </p>
          <input
            ref={inputRef}
            type="text"
            value={canvasName}
            onChange={(e) => setCanvasName(e.target.value)}
            onKeyDown={handleKeyDown}
            onPointerDown={handleInputPointerDown}
            placeholder={placeholder}
            className="canvas-name-input"
          />
          <div className="canvas-name-actions">
            <button 
              className="canvas-name-cancel-btn"
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              className="canvas-name-save-btn"
              onClick={handleSave}
              disabled={!canvasName.trim() || isLoading}
            >
              {isLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{
                      animation: 'spin 1s linear infinite'
                    }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Saving...
                </div>
              ) : (
                'Save & Continue'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default CanvasNameModal;
